import React, { useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { ChevronDown } from "lucide-react";
import { useMarketingSettings, useUpdateMarketingSettings } from "@/hooks/sms/useMarketingSettings";
import { useDisclosures, useSaveDisclosure } from "@/hooks/sms/useDisclosures";
import type { DisclosureKind, MarketingSettings } from "@/types/sms";

/** 0-23 → "8:00 AM" / "9:00 PM" / "12:00 AM", so the quiet-hours selects read
 *  in plain language instead of a 24-hour number. */
function formatHourLabel(hour: number): string {
  const h = hour % 24;
  const period = h < 12 ? "AM" : "PM";
  const displayHour = h % 12 === 0 ? 12 : h % 12;
  return `${displayHour}:00 ${period}`;
}

const START_HOURS = Array.from({ length: 24 }, (_, h) => h);
const END_HOURS = Array.from({ length: 24 }, (_, h) => h + 1);

const COMPLIANCE_DISCLOSURE_KINDS: { kind: DisclosureKind; label: string }[] = [
  { kind: "welcome_footer", label: "Welcome footer" },
  { kind: "help", label: "Help text" },
  { kind: "stop_confirmation", label: "Stop confirmation" },
  { kind: "join_disclosure", label: "Join disclosure" },
];

/** Shared local-form state for the marketing settings cards. Each card owns
 *  only the slice of fields it saves, so splitting the old single card into
 *  section cards keeps identical save behaviour (each Save button still writes
 *  just its own fields). `useMarketingSettings` is React-Query cached, so the
 *  several cards sharing this hook read one cached row, not one fetch each. */
function useSettingsForm() {
  const { data: settings } = useMarketingSettings();
  const updateSettings = useUpdateMarketingSettings();
  const [form, setForm] = useState<Partial<MarketingSettings>>({});

  useEffect(() => {
    if (settings) setForm(settings);
  }, [settings]);

  const set = <K extends keyof MarketingSettings>(key: K, value: MarketingSettings[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  const save = (fields: (keyof MarketingSettings)[]) => {
    if (!settings) return;
    const updates: Partial<MarketingSettings> & { store_id: string } = { store_id: settings.store_id };
    for (const field of fields) {
      (updates as Record<string, unknown>)[field] = form[field];
    }
    updateSettings.mutate(updates);
  };

  return { settings, form, set, save, updateSettings };
}

/** One disclosure textarea: shows the saved version, edits locally, and
 *  saves as a brand new version (disclosures are append-only, CP-4). */
const DisclosureField = ({ kind, label }: { kind: DisclosureKind; label: string }) => {
  const { data: disclosures } = useDisclosures();
  const saveDisclosure = useSaveDisclosure();
  const current = disclosures?.[kind];
  const [body, setBody] = useState(current?.body ?? "");
  const [historyOpen, setHistoryOpen] = useState(false);

  useEffect(() => {
    setBody(current?.body ?? "");
  }, [current?.body]);

  const savedDate = current ? new Date(current.created_at).toLocaleDateString() : null;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <Label htmlFor={`disclosure-${kind}`}>{label}</Label>
        {current && (
          <span className="text-xs text-muted-foreground">
            Version {current.version}{savedDate ? `, saved ${savedDate}` : ""}
          </span>
        )}
      </div>
      <Textarea
        id={`disclosure-${kind}`}
        className="text-base"
        value={body}
        onChange={(e) => setBody(e.target.value)}
        rows={2}
      />
      <div className="flex items-center justify-between gap-2">
        <Button
          type="button"
          variant="outline"
          className="h-11 sm:h-10"
          disabled={saveDisclosure.isPending || body === current?.body}
          onClick={() => saveDisclosure.mutate({ kind, body })}
        >
          Save {label.toLowerCase()}
        </Button>
        <Collapsible open={historyOpen} onOpenChange={setHistoryOpen}>
          <CollapsibleTrigger asChild>
            <button type="button" className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground">
              History
              <ChevronDown className={`h-3 w-3 transition-transform ${historyOpen ? "rotate-180" : ""}`} />
            </button>
          </CollapsibleTrigger>
          <CollapsibleContent>
            {current && (
              <p className="mt-2 text-xs text-muted-foreground">
                Version {current.version}{savedDate ? ` — saved ${savedDate}` : ""}: {current.body}
              </p>
            )}
          </CollapsibleContent>
        </Collapsible>
      </div>
    </div>
  );
};

/** Store profile — the business name, address, hours, and the owner's test
 *  number, shown to customers on offer pages and in texts. */
export const StoreProfileCard = () => {
  const { settings, form, set, save, updateSettings } = useSettingsForm();
  if (!settings) return null;

  return (
    <Card>
      <CardContent className="space-y-4 p-4 sm:p-6">
        <h3 className="text-base font-semibold text-foreground">Store profile</h3>
        <p className="text-sm text-muted-foreground">
          What customers see: the business name, address, and hours shown on offer pages and in texts.
        </p>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="store-name">Store name</Label>
            <Input id="store-name" className="h-11" value={form.store_name ?? ""} onChange={(e) => set("store_name", e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="store-phone">Store phone</Label>
            <Input id="store-phone" className="h-11" value={form.store_phone ?? ""} onChange={(e) => set("store_phone", e.target.value)} />
          </div>
          <div className="space-y-1.5 sm:col-span-2">
            <Label htmlFor="store-address">Store address</Label>
            <Input id="store-address" className="h-11" value={form.store_address ?? ""} onChange={(e) => set("store_address", e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="store-hours">Store hours</Label>
            <Input id="store-hours" className="h-11" value={form.store_hours ?? ""} onChange={(e) => set("store_hours", e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="owner-test-phone">Send test to me</Label>
            <Input id="owner-test-phone" className="h-11" value={form.owner_test_phone ?? ""} onChange={(e) => set("owner_test_phone", e.target.value)} />
          </div>
        </div>
        <p className="text-xs text-muted-foreground">Times are in Pacific Time.</p>
        <Button
          type="button"
          className="h-11 sm:h-10"
          disabled={updateSettings.isPending}
          onClick={() => save(["store_name", "store_phone", "store_address", "store_hours", "owner_test_phone"])}
        >
          Save store profile
        </Button>
      </CardContent>
    </Card>
  );
};

/** Sending rules — quiet hours, per-customer and daily caps, and send rate. */
export const SendingRulesCard = () => {
  const { settings, form, set, save, updateSettings } = useSettingsForm();
  if (!settings) return null;

  return (
    <Card>
      <CardContent className="space-y-4 p-4 sm:p-6">
        <h3 className="text-base font-semibold text-foreground">Sending rules</h3>
        <p className="text-sm text-muted-foreground">
          Quiet hours, how many texts a customer can get, and how fast texts go out.
        </p>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="quiet-hours-start">Quiet hours start</Label>
            <Select
              value={String(form.quiet_hours_start ?? 8)}
              onValueChange={(v) => set("quiet_hours_start", parseInt(v, 10))}
            >
              <SelectTrigger id="quiet-hours-start" className="h-11">
                <SelectValue>{formatHourLabel(form.quiet_hours_start ?? 8)}</SelectValue>
              </SelectTrigger>
              <SelectContent>
                {START_HOURS.map((h) => (
                  <SelectItem key={h} value={String(h)}>{formatHourLabel(h)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="quiet-hours-end">Quiet hours end</Label>
            <Select
              value={String(form.quiet_hours_end ?? 21)}
              onValueChange={(v) => set("quiet_hours_end", parseInt(v, 10))}
            >
              <SelectTrigger id="quiet-hours-end" className="h-11">
                <SelectValue>{formatHourLabel(form.quiet_hours_end ?? 21)}</SelectValue>
              </SelectTrigger>
              <SelectContent>
                {END_HOURS.map((h) => (
                  <SelectItem key={h} value={String(h)}>{formatHourLabel(h)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="frequency-cap-messages">Texts per customer</Label>
            <Input
              id="frequency-cap-messages"
              type="number"
              min={1}
              className="h-11"
              value={form.frequency_cap_messages ?? 2}
              onChange={(e) => set("frequency_cap_messages", parseInt(e.target.value, 10) || 1)}
            />
            <p className="text-xs leading-relaxed text-muted-foreground">
              Keep this at or below what the welcome text promises — currently "up to 2 msgs/wk".
            </p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="frequency-cap-days">Per how many days</Label>
            <Input
              id="frequency-cap-days"
              type="number"
              min={1}
              className="h-11"
              value={form.frequency_cap_days ?? 7}
              onChange={(e) => set("frequency_cap_days", parseInt(e.target.value, 10) || 1)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="daily-send-cap">Daily send cap</Label>
            <Input
              id="daily-send-cap"
              type="number"
              min={1}
              className="h-11"
              value={form.daily_send_cap ?? 1000}
              onChange={(e) => set("daily_send_cap", parseInt(e.target.value, 10) || 1)}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="send-rate-per-minute">Sends per minute</Label>
            <Input
              id="send-rate-per-minute"
              type="number"
              min={1}
              className="h-11"
              value={form.send_rate_per_minute ?? 35}
              onChange={(e) => set("send_rate_per_minute", parseInt(e.target.value, 10) || 1)}
            />
          </div>
        </div>
        <div className="flex items-start justify-between gap-4 border-t border-border pt-4">
          <div className="min-w-0">
            <Label htmlFor="use-a2p-batches" className="text-sm font-medium text-foreground">
              Use RingCentral high-volume batches
            </Label>
            <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
              Turn on only after RingCentral confirms High Volume SMS on this number.
            </p>
          </div>
          <Switch
            id="use-a2p-batches"
            checked={form.use_a2p_batches ?? false}
            onCheckedChange={(checked) => set("use_a2p_batches", checked)}
          />
        </div>
        <Button
          type="button"
          className="h-11 sm:h-10"
          disabled={updateSettings.isPending}
          onClick={() =>
            save([
              "quiet_hours_start",
              "quiet_hours_end",
              "frequency_cap_messages",
              "frequency_cap_days",
              "daily_send_cap",
              "send_rate_per_minute",
              "use_a2p_batches",
            ])
          }
        >
          Save sending rules
        </Button>
      </CardContent>
    </Card>
  );
};

/** Activity — how recently a customer must have ordered to count as active. */
export const ActivityWindowCard = () => {
  const { settings, form, set, save, updateSettings } = useSettingsForm();
  if (!settings) return null;

  return (
    <Card>
      <CardContent className="space-y-4 p-4 sm:p-6">
        <h3 className="text-base font-semibold text-foreground">Activity</h3>
        <p className="text-sm text-muted-foreground">
          How recently a customer must have ordered to count as active in reports and segments.
        </p>
        <div className="space-y-1.5">
          <Label htmlFor="activity-window-days">Active-user window (days)</Label>
          <Input
            id="activity-window-days"
            type="number"
            min={7}
            max={365}
            className="h-11"
            value={form.activity_window_days ?? 90}
            onChange={(e) => set("activity_window_days", parseInt(e.target.value, 10) || 90)}
          />
        </div>
        <Button
          type="button"
          className="h-11 sm:h-10"
          disabled={updateSettings.isPending}
          onClick={() => save(["activity_window_days"])}
        >
          Save activity window
        </Button>
      </CardContent>
    </Card>
  );
};

/** Compliance — the 10DLC campaign id and the versioned disclosure wording. */
export const ComplianceCard = () => {
  const { settings, form, set, save, updateSettings } = useSettingsForm();
  if (!settings) return null;

  return (
    <Card>
      <CardContent className="space-y-6 p-4 sm:p-6">
        <div>
          <h3 className="text-base font-semibold text-foreground">Compliance</h3>
          <p className="text-sm text-muted-foreground">
            The 10DLC campaign id and the exact wording customers see for consent, help, and opting out.
          </p>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="tendlc-campaign-id">10DLC campaign id</Label>
          <Input
            id="tendlc-campaign-id"
            className="h-11"
            value={form.tendlc_campaign_id ?? ""}
            onChange={(e) => set("tendlc_campaign_id", e.target.value)}
          />
          <Button
            type="button"
            variant="outline"
            className="h-11 sm:h-10"
            disabled={updateSettings.isPending}
            onClick={() => save(["tendlc_campaign_id"])}
          >
            Save campaign id
          </Button>
        </div>
        {COMPLIANCE_DISCLOSURE_KINDS.map(({ kind, label }) => (
          <DisclosureField key={kind} kind={kind} label={label} />
        ))}
      </CardContent>
    </Card>
  );
};

/** Links — the portal's own domain and the online-ordering link. */
export const LinksCard = () => {
  const { settings, form, set, save, updateSettings } = useSettingsForm();
  if (!settings) return null;

  return (
    <Card>
      <CardContent className="space-y-4 p-4 sm:p-6">
        <h3 className="text-base font-semibold text-foreground">Links</h3>
        <p className="text-sm text-muted-foreground">
          The portal's own domain used in every text — no third-party shorteners.
        </p>
        <div className="space-y-1.5">
          <Label htmlFor="public-base-url">Public base URL</Label>
          <Input id="public-base-url" className="h-11" value={form.public_base_url ?? ""} onChange={(e) => set("public_base_url", e.target.value)} />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="online-ordering-url">Online ordering URL</Label>
          <Input id="online-ordering-url" className="h-11" value={form.online_ordering_url ?? ""} onChange={(e) => set("online_ordering_url", e.target.value)} />
        </div>
        <div className="flex items-start justify-between gap-4">
          <div className="min-w-0">
            <Label htmlFor="append-promo-code-param" className="text-sm font-medium text-foreground">
              Append promo code to the ordering link
            </Label>
            <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
              Adds the offer code as a URL parameter so the ordering site can apply it automatically.
            </p>
          </div>
          <Switch
            id="append-promo-code-param"
            checked={form.append_promo_code_param ?? false}
            onCheckedChange={(checked) => set("append_promo_code_param", checked)}
          />
        </div>
        {form.append_promo_code_param && (
          <div className="space-y-1.5">
            <Label htmlFor="promo-code-param-name">Parameter name</Label>
            <Input
              id="promo-code-param-name"
              className="h-11"
              value={form.promo_code_param_name ?? "promo"}
              onChange={(e) => set("promo_code_param_name", e.target.value)}
            />
          </div>
        )}
        <Button
          type="button"
          className="h-11 sm:h-10"
          disabled={updateSettings.isPending}
          onClick={() => save(["public_base_url", "online_ordering_url", "append_promo_code_param", "promo_code_param_name"])}
        >
          Save links
        </Button>
      </CardContent>
    </Card>
  );
};
